import type { DashboardTile } from '@/features/dashboard-tiles/types'
import { mapLegacyDashboardPath } from '@/config/routes'
import { isReimbursementsAccessDeniedRole } from '@/features/reimbursements/utils/reimbursementsAccess'
import { showValidationMessage } from '@/lib/utils/notifications'

/** Angular `clickTile()` opens the EORP popup when the tile URL is the agreement marker. */
const EORP_TILE_MARKER = 'explanation-service-aggrement'

function isReimbursementsTileUrl(url: string): boolean {
  const path = normalizeRelativePath(url).split('?')[0]
  return (
    path === '/defaults' ||
    path.startsWith('/defaults/') ||
    path === '/dashboard/reimbursements' ||
    path.startsWith('/dashboard/reimbursements/')
  )
}

/**
 * Angular / legacy tile URLs that still need a rewrite after route parity.
 * Most Angular paths now match React 1:1; keep only true renames / regroupings.
 */
const LEGACY_TILE_TO_REACT: Array<{
  legacyPrefix: string
  reactPrefix: string
}> = [
  { legacyPrefix: '/config/underwriting', reactPrefix: '/underwriting-config' },
  {
    legacyPrefix: '/gdpr-consent/gdpr-document',
    reactPrefix: '/gdpr-consent/gdpr-document',
  },
  { legacyPrefix: '/terms-and-conditions', reactPrefix: '/terms-and-conditions' },
  { legacyPrefix: '/transfer-list', reactPrefix: '/transfer-list' },
  { legacyPrefix: '/import-data', reactPrefix: '/import-data' },
  { legacyPrefix: '/sales-team', reactPrefix: '/sales-team' },
  { legacyPrefix: '/property-maintenance', reactPrefix: '/property-maintenance' },
  { legacyPrefix: '/screening-info-requests', reactPrefix: '/screening-info-requests' },
  { legacyPrefix: '/frequently-used-notes', reactPrefix: '/frequently-used-notes' },
  { legacyPrefix: '/underwriting-config', reactPrefix: '/underwriting-config' },
  { legacyPrefix: '/stock', reactPrefix: '/stock' },
  { legacyPrefix: '/version', reactPrefix: '/version' },
  { legacyPrefix: '/user-roles', reactPrefix: '/user-roles' },
  { legacyPrefix: '/yardiinvite', reactPrefix: '/yardiinvite' },
  { legacyPrefix: '/company', reactPrefix: '/company' },
  { legacyPrefix: '/property', reactPrefix: '/property' },
  { legacyPrefix: '/product', reactPrefix: '/product' },
  { legacyPrefix: '/invite', reactPrefix: '/invite' },
  { legacyPrefix: '/application', reactPrefix: '/application' },
  { legacyPrefix: '/resident', reactPrefix: '/resident' },
  { legacyPrefix: '/defaults', reactPrefix: '/defaults' },
  { legacyPrefix: '/reports-scheduler', reactPrefix: '/reports-scheduler' },
  { legacyPrefix: '/reports', reactPrefix: '/reports' },
  { legacyPrefix: '/user-management', reactPrefix: '/user-management' },
  { legacyPrefix: '/email-templates', reactPrefix: '/email-templates' },
  { legacyPrefix: '/dashboard-tiles', reactPrefix: '/dashboard-tiles' },
  { legacyPrefix: '/notification-settings', reactPrefix: '/notification-settings' },
  { legacyPrefix: '/policy', reactPrefix: '/policy' },
  { legacyPrefix: '/organizations', reactPrefix: '/organizations' },
  { legacyPrefix: '/mailtrap', reactPrefix: '/mailtrap' },
  { legacyPrefix: '/impact-notes', reactPrefix: '/impact-notes' },
  { legacyPrefix: '/report-description', reactPrefix: '/report-description' },
  { legacyPrefix: '/system-announcement', reactPrefix: '/system-announcement' },
  { legacyPrefix: '/jobs/', reactPrefix: '/jobs/' },
  { legacyPrefix: '/config', reactPrefix: '/config' },
  { legacyPrefix: '/help-about', reactPrefix: '/help-about' },
  { legacyPrefix: '/imap-logs', reactPrefix: '/imap-logs' },
]

/** True when the current role's placeholders are present. Mirrors Angular's string replace. */
export function resolveTileUrl(
  url: string,
  companyId?: number | null,
  propertyId?: number | null,
): string {
  let next = url ?? ''
  if (companyId != null && next.includes('COMPANY_ID')) {
    next = next.replace('COMPANY_ID', String(companyId))
  }
  if (propertyId != null && next.includes('PROPERTY_ID')) {
    next = next.replace('PROPERTY_ID', String(propertyId))
  }
  return next
}

/** Angular special-cases the reimbursement agreement tile to open a popup instead of navigating. */
export function isEorpTile(tile: DashboardTile): boolean {
  return typeof tile.tile_url === 'string' && tile.tile_url.includes(EORP_TILE_MARKER)
}

/** Angular stores `new_tab` as '1'/'0'; tolerate boolean/number too. */
export function tileOpensInNewTab(tile: DashboardTile): boolean {
  return tile.new_tab === '1' || tile.new_tab === 1 || tile.new_tab === true
}

function normalizeRelativePath(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('/')) return trimmed
  return `/${trimmed}`
}

/**
 * Map a stored legacy (or already-React) tile path to an in-app React route.
 * Returns null when no React route owns the path.
 */
export function mapTileUrlToReactPath(rawUrl: string): string | null {
  const path = normalizeRelativePath(rawUrl)
  if (!path) return null

  if (path === '/dashboard') return path

  if (path.startsWith('/dashboard/')) {
    return mapLegacyDashboardPath(path)
  }

  if (path.startsWith('/applicant/') || path === '/applicant') {
    return path
  }

  for (const rule of LEGACY_TILE_TO_REACT) {
    if (path === rule.legacyPrefix || path.startsWith(`${rule.legacyPrefix}?`)) {
      const query = path.includes('?') ? path.slice(path.indexOf('?')) : ''
      return `${rule.reactPrefix}${query}`
    }
    if (path.startsWith(rule.legacyPrefix)) {
      const rest = path.slice(rule.legacyPrefix.length)
      return `${rule.reactPrefix}${rest}`
    }
  }

  return null
}

function openHref(href: string, newTab: boolean): void {
  if (newTab) {
    window.open(href, '_blank', 'noopener,noreferrer')
  } else {
    window.location.href = href
  }
}

/**
 * Open a dashboard tile — mirrors Angular `DashboardTileReportComponent.clickTile`:
 * absolute/mailto/tel URLs open as-is, known paths map to their React route, and
 * EXTERNAL_INSURANCE / ROLE_LIBERTY_FIN_MGR cannot open Reimbursements
 * (Access Denied + stay on dashboard). Unmapped paths stay on the dashboard.
 */
export function openDashboardTile(
  tile: DashboardTile,
  options?: { roleName?: string | null },
): void {
  const url = (tile.tile_url ?? '').trim()
  if (!url) return
  if (url.includes(EORP_TILE_MARKER)) return

  if (isReimbursementsAccessDeniedRole(options?.roleName) && isReimbursementsTileUrl(url)) {
    showValidationMessage('Access Denied', 'Validation Message')
    return
  }

  const newTab = tileOpensInNewTab(tile)

  if (/^https?:\/\//i.test(url) || /^mailto:/i.test(url) || /^tel:/i.test(url)) {
    openHref(url, newTab)
    return
  }

  if (/^\/\//.test(url)) {
    openHref(`https:${url}`, newTab)
    return
  }

  const reactPath = mapTileUrlToReactPath(url)
  if (!reactPath) return

  openHref(reactPath, newTab)
}
